/**
 * PWA cache policy — the auditable half of the service worker (T52).
 *
 * This module is deliberately *outside* the Workbox configuration. The contract
 * (`_docs/01-core/pwa-contract.md` §3) requires the rule that keeps a member's private
 * data off her device to be readable and unit-testable on its own, rather than inferred
 * from plugin config. `app/sw/sw.ts` consumes this; it does not restate it.
 *
 * Pure: no IO, no worker globals, no clock. Importable from tests and from the worker.
 */

import {
  ROUTE_CONFIG,
  SUPPORTED_LANGS,
  type CacheStrategy,
  type RouteKey,
} from "~/config/route-config";

export type { CacheStrategy };

/**
 * Paths that must never reach any cache, whatever a route strategy says.
 *
 * This is not redundant with `cacheStrategy: 'none'`. Route strategies only cover keys in
 * `ROUTE_CONFIG`; these patterns also cover surfaces that live outside it (`/api/*`,
 * `/admin/*`, `/media/*`) and pin the ones whose mis-classification would be most costly,
 * so a future edit to a route's strategy cannot silently expose them.
 */
export const NEVER_CACHE: readonly RegExp[] = [
  // Every API route: mutations, webhooks, member-scoped reads, admin exports.
  // Allow-listing individual endpoints later is a deliberate ticket, not a tweak.
  /^\/api\//,
  // Back office.
  /^\/admin(\/|$)/,
  // 302s to signed R2 URLs that expire — a cached redirect is a guaranteed broken video.
  /^\/media\//,
  // LLM endpoints: a cached response is a wrong answer, not a stale one. Forward guard —
  // these are server-side today, and this keeps that true if one is ever exposed.
  /^\/api\/llm/,
  // The member's own writing.
  /^\/(es\/panel\/diario|en\/dashboard\/journal)(\/|$)/,
  // Account, including the security and privacy tabs.
  /^\/(es\/perfil|en\/profile)(\/|$)/,
  // Payment surfaces.
  /^\/(es\/comprar|en\/checkout)(\/|$)/,
  // Credential flows — a cached and replayed auth response is a session-fixation surface.
  /^\/(es\/registro|en\/signup)(\/|$)/,
  /^\/(es\/restablecer-contrasena|en\/reset-password)(\/|$)/,
  /^\/(es\/recuperar-acceso|en\/recover-access)(\/|$)/,
  /^\/(es\/verificar-email|en\/verify-email)(\/|$)/,
];

/** `/es/panel/modulo/:id` → `^/es/panel/modulo/[^/]+$` */
function pathToPattern(path: string): RegExp {
  const source = path
    .split("/")
    .map((segment) =>
      segment.startsWith(":")
        ? "[^/]+"
        : segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    )
    .join("/");
  return new RegExp(`^${source}$`);
}

const ROUTE_MATCHERS: readonly {
  readonly key: RouteKey;
  readonly pattern: RegExp;
  readonly strategy: CacheStrategy;
  readonly isPrivate: boolean;
}[] = (Object.entries(ROUTE_CONFIG) as [RouteKey, (typeof ROUTE_CONFIG)[RouteKey]][])
  .flatMap(([key, definition]) =>
    SUPPORTED_LANGS.map((lang) => ({
      key,
      pattern: pathToPattern(definition.paths[lang]),
      strategy: definition.cacheStrategy as CacheStrategy,
      isPrivate: definition.isPrivate,
    })),
  );

/**
 * Strips the query string, the React Router v7 single-fetch `.data` suffix, and a
 * trailing slash, so `/es/faq/?x=1` and `/es/faq.data` both normalize to `/es/faq`.
 */
export function normalizePath(pathname: string): string {
  const withoutQuery = pathname.split(/[?#]/)[0] ?? "";
  const withoutData = withoutQuery.replace(/\.data$/, "");
  if (withoutData.length > 1 && withoutData.endsWith("/")) {
    return withoutData.slice(0, -1);
  }
  return withoutData;
}

/** True when the path matches any `NEVER_CACHE` pattern. Checked before anything else. */
export function isNeverCached(pathname: string): boolean {
  const path = normalizePath(pathname);
  return NEVER_CACHE.some((pattern) => pattern.test(path));
}

/**
 * The route's declared strategy, or `'none'` for anything not in `ROUTE_CONFIG`.
 *
 * Unknown paths default to `'none'` on purpose: a surface nobody classified is a surface
 * nobody thought about, and the safe answer for those is to fetch it every time.
 */
export function strategyForPath(pathname: string): CacheStrategy {
  const path = normalizePath(pathname);
  if (isNeverCached(path)) return "none";
  return ROUTE_MATCHERS.find((matcher) => matcher.pattern.test(path))?.strategy ?? "none";
}

/**
 * T895 (F5) — is this a translation file (`/locales/{es,en}/<ns>.json`)? Those URLs are stable
 * and some are precached for the offline page (T841), so served cache-first a new bundle met
 * the previous build's copy and showed raw keys. The worker serves them network-first and
 * reads the precache only when the network fails.
 */
export function isNetworkFirstLocalePath(pathname: string): boolean {
  return /^\/locales\/(es|en)\/[\w-]+\.json$/.test(pathname);
}

/**
 * T895 (F5) — the worker's answer for a translation file: the network's copy whenever the
 * network answers, the precached one only when it does not, and the original error when
 * neither exists. Injected so the order is tested, not read off the worker's source.
 */
export async function networkFirstThenPrecache(
  request: Request,
  deps: {
    fetch: (request: Request) => Promise<Response>;
    matchPrecache: (key: string) => Promise<Response | undefined>;
  },
): Promise<Response> {
  try {
    return await deps.fetch(request);
  } catch (error) {
    const cached = await deps.matchPrecache(new URL(request.url).pathname);
    if (cached) return cached;
    throw error;
  }
}

/**
 * May the navigation document for this path be cached?
 *
 * Path only: whether a given response may be written is `isCacheableDocumentResponse`'s
 * call (T885 — a page rendered with a session never is). Auth is re-validated on the server
 * on every open.
 */
export function isCacheableDocument(pathname: string): boolean {
  // A `.data` URL is never a document, even when she opens it directly (T885 audit F1):
  // `normalizePath` would map `/en/method.data` to Método, and the body is root's data.
  if (/\.data$/.test(pathname.split(/[?#]/)[0] ?? "")) return false;
  const strategy = strategyForPath(pathname);
  return strategy === "shell" || strategy === "data-whitelist";
}

/**
 * May this path's loader data (`.data`) be cached? Never.
 *
 * T885: a single-fetch `.data` body carries the root loader's data too — root revalidates
 * on every navigation — so every body holds `sessionUser` (her name and e-mail), and the
 * cache key (the URL) is the same for a guest and a member. Cached, it painted the previous
 * member in the public header after sign-out. There is no `.data` body without identity to
 * cache, so `data-whitelist` routes cache their document only, like `shell`.
 *
 * Deny still runs first (R-T52-1): if a future change ever lets some `.data` back in, a
 * `NEVER_CACHE` match must keep winning over it.
 */
export function isCacheableDataPath(pathname: string): boolean {
  if (isNeverCached(pathname)) return false;
  return false;
}

/**
 * True when the path is a member surface — a route `ROUTE_CONFIG` marks `isPrivate`.
 *
 * Lives here rather than in `install-eligibility.ts` because this module already owns the
 * `ROUTE_CONFIG` → path lookup. A second classification of "what is private" would be a
 * second source of truth about the most security-relevant fact in the file, and the two
 * would drift the first time a route moved.
 *
 * T52 iteration 4 (F1): `shouldOfferInstall` had no surface input, so the install card
 * offered itself on `/en` — the sales landing — to a logged-out visitor. The arming signal
 * is `localStorage`, which is per-origin and never cleared at sign-out, so a member who
 * once finished a ritual kept triggering it all over the public site. The contract's own
 * words: asking someone to install an app she has not yet used is how install prompts
 * earn their reputation.
 *
 * Unknown paths are NOT member surfaces. Same posture as `strategyForPath`: a route nobody
 * classified is a route nobody thought about, and the quiet answer is the safe one.
 */
export function isMemberSurface(pathname: string): boolean {
  const path = normalizePath(pathname);
  return ROUTE_MATCHERS.find((matcher) => matcher.pattern.test(path))?.isPrivate ?? false;
}

/** The navigation-document cache. `-v2` since T885: it only ever holds identity-free pages. */
export const DOCUMENT_CACHE = "tfc-documents-v2";

/**
 * Caches a pre-T885 worker filled with bodies that carry a member's identity: `.data`
 * responses and documents rendered for a signed-in member. No route writes them any more;
 * the worker deletes them on `activate`, and the page deletes them on an identity change
 * while an old worker may still be running (`session-cache-reset.ts`).
 */
export const LEGACY_CACHES: readonly string[] = ["tfc-documents", "tfc-route-data"];

/**
 * Set by `entry.server.tsx` on a document rendered with a session: its HTML embeds root's
 * `sessionUser`. The value says only that an identity is present, never whose.
 */
export const IDENTITY_HEADER = "X-TFC-Identity";

/**
 * May this navigation response enter `DOCUMENT_CACHE`? Only an HTML 200 rendered without a
 * session. A cached page is served offline to whoever opens the device next — so a page
 * carrying one woman's name and e-mail is never written, whichever
 * way her session started or ended (T885: a sign-in that lands on a full page load, Google
 * or the verify-email link, is invisible to any purge the page runs).
 */
export function isCacheableDocumentResponse(response: Pick<Response, "status" | "headers">): boolean {
  return (
    response.status === 200 &&
    // Only an HTML document went through entry.server and so carries the marker when it
    // should; anything else (a `.data` body, `text/x-script`) cannot be vouched for.
    (response.headers.get("content-type") ?? "").startsWith("text/html") &&
    !response.headers.has(IDENTITY_HEADER)
  );
}

/** True when the root loader's data for this render carries a signed-in `sessionUser`. */
export function documentCarriesIdentity(rootLoaderData: unknown): boolean {
  return (
    typeof rootLoaderData === "object" &&
    rootLoaderData !== null &&
    (rootLoaderData as { sessionUser?: unknown }).sessionUser != null
  );
}
